import { useState } from 'react';
import { FaSpinner } from 'react-icons/fa';
import useFinancialStatement from '../../Hooks/Users/useFinancialStatement';
import FilterInput from '../../Components/FilterInput';
import { formatDateOnly } from '../../Utils/dateFormatter';
import { formatDate } from '../../Utils/dateFormatter';
import Button from '../../Components/Button';

const FinancialStatement = ({ user }) => {
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [appliedDateFrom, setAppliedDateFrom] = useState(null);
  const [appliedDateTo, setAppliedDateTo] = useState(null);

  const { data, loading, error } = useFinancialStatement(user?.id, appliedDateFrom, appliedDateTo);

  const handleApplyFilter = () => {
    setAppliedDateFrom(dateFrom || null);
    setAppliedDateTo(dateTo || null);
  };

  const handleClearFilter = () => {
    setDateFrom('');
    setDateTo('');
    setAppliedDateFrom(null);
    setAppliedDateTo(null);
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="text-center">
          <FaSpinner className="animate-spin text-main1 text-2xl mx-auto mb-4 " />
          <p className="text-main1 font-cairo">جاري تحميل البيانات...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="p-4 overflow-auto scrollbar-hidden">
      {/* فلتر التاريخ */}
      <div className="flex items-center gap-2 mb-6">
        <FilterInput
          label="من تاريخ"
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          placeholder="YYYY-MM-DD"
        />
        <FilterInput
          label="إلى تاريخ"
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          placeholder="YYYY-MM-DD"
        />
        <Button onClick={handleApplyFilter} className="whitespace-nowrap">
          إرسال
        </Button>
        {(appliedDateFrom || appliedDateTo) && (
          <button
            onClick={handleClearFilter}
            className="text-xs rounded-xl text-main1 bg-grayish hover:bg-opacity-90 transition px-3 py-2 whitespace-nowrap"
          >
            مسح الفلترة
          </button>
        )}
      </div>

      {/* ملخص الحساب */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-blue-50 p-4 rounded-lg">
          <p className="text-sm text-gray-600 mb-1">الرصيد الابتدائي</p>
          <p className="text-2xl font-bold text-main1">{data.initial_balance.toLocaleString()}</p>
        </div>
        <div className="bg-green-50 p-4 rounded-lg">
          <p className="text-sm text-gray-600 mb-1">الرصيد النهائي</p>
          <p className="text-2xl font-bold text-green-700">{data.final_balance.toLocaleString()}</p>
        </div>
        <div className="bg-purple-50 p-4 rounded-lg">
          <p className="text-sm text-gray-600 mb-1">عدد المعاملات</p>
          <p className="text-2xl font-bold text-purple-700">{data.transactions.length}</p>
        </div>
      </div>

      {/* الفترة الزمنية */}
      <div className="bg-grayish p-4 rounded-lg mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-500">من تاريخ</p>
            <p className="font-bold text-main1">{formatDateOnly(data.date_from)}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">إلى تاريخ</p>
            <p className="font-bold text-main1">{formatDateOnly(data.date_to)}</p>
          </div>
        </div>
      </div>

      {/* جدول المعاملات */}
      <div className="overflow-x-auto rounded-2xl shadow-md bg-transparent">
        <table className="min-w-full text-sm text-main1 font-cairo">
          <thead>
            <tr className="bg-grayish text-main1">
              <th className="p-4 text-center">التاريخ</th>
              <th className="p-4 text-center">النوع</th>
              <th className="p-4 text-center">الخدمة</th>
              <th className="p-4 text-center">الوصف</th>
              <th className="p-4 text-center">المبلغ</th>
              <th className="p-4 text-center">الرصيد بعد</th>
            </tr>
          </thead>
          <tbody>
            {data.transactions.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center py-8 text-gray-400">
                  لا توجد معاملات في هذه الفترة
                </td>
              </tr>
            ) : (
              [...data.transactions].reverse().map((transaction, index) => (
                <tr key={index} className="border-b last:border-b-0 hover:bg-grayish/60 transition">
                  <td className="p-4 text-center whitespace-pre-line">{formatDate(transaction.date)}</td>
                  <td className="p-4 text-center">
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${transaction.type === 'wallet_transaction'
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-green-100 text-green-700'
                      }`}>
                      {transaction.type === 'wallet_transaction' ? 'محفظة' : 'خدمة'}
                    </span>
                  </td>
                  <td className="p-4 text-center text-xs">{transaction.service_type}</td>
                  <td className="p-4 text-right text-xs">{transaction.description}</td>
                  <td className="p-4 text-center">
                    <span className={`font-bold ${transaction.amount_used < 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {transaction.amount_used < 0 ? '+' : '-'}{Math.abs(transaction.amount_used).toLocaleString()}
                    </span>
                  </td>
                  <td className="p-4 text-center font-bold text-main1">
                    {transaction.balance_after.toLocaleString()}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FinancialStatement;
